import { ParsedUserRow } from './excel-parser';

export interface ValidationError {
  row: number;
  field: string;
  message: string;
}

export interface ValidatedUser extends ParsedUserRow {
  validationErrors: ValidationError[];
  isValid: boolean;
}

const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validatePhone = (phone: string): boolean => {
  // Remove spaces and special characters
  const cleaned = phone.replace(/[\s\-\(\)]/g, '');
  // Check if it's a valid number (10 digits for Indian numbers)
  return /^\d{10}$/.test(cleaned);
};

const parseDate = (dateStr: string): Date | null => {
  if (!dateStr) return null;
  
  // Try different date formats
  const formats = [
    // YYYY-MM-DD
    /^(\d{4})-(\d{2})-(\d{2})$/,
    // DD/MM/YYYY
    /^(\d{2})\/(\d{2})\/(\d{4})$/,
    // DD-MM-YYYY
    /^(\d{2})-(\d{2})-(\d{4})$/
  ];

  for (const format of formats) {
    const match = dateStr.match(format);
    if (match) {
      let year, month, day;
      if (format === formats[0]) {
        // YYYY-MM-DD
        [, year, month, day] = match;
      } else {
        // DD/MM/YYYY or DD-MM-YYYY
        [, day, month, year] = match;
      }
      
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
  }
  
  return null;
};

export const validateUsers = (users: ParsedUserRow[]): ValidatedUser[] => {
  return users.map(user => {
    const errors: ValidationError[] = [];
    const rowNum = user.row_number || 0;

    // Validate name (required)
    if (!user.name || user.name.trim() === '') {
      errors.push({
        row: rowNum,
        field: 'name',
        message: 'Name is required'
      });
    }

    // Validate email (optional but must be valid if provided)
    if (user.email && !validateEmail(user.email)) {
      errors.push({
        row: rowNum,
        field: 'email',
        message: 'Invalid email format'
      });
    }

    // Validate phone (optional but must be valid if provided)
    if (user.phone && !validatePhone(user.phone)) {
      errors.push({
        row: rowNum,
        field: 'phone',
        message: 'Invalid phone number (must be 10 digits)'
      });
    }

    // Validate WhatsApp (optional but must be valid if provided)
    if (user.whatsapp && !validatePhone(user.whatsapp)) {
      errors.push({
        row: rowNum,
        field: 'whatsapp',
        message: 'Invalid WhatsApp number (must be 10 digits)'
      });
    }

    // Validate dates
    if (user.start_date) {
      const startDate = parseDate(user.start_date);
      if (!startDate) {
        errors.push({
          row: rowNum,
          field: 'start_date',
          message: 'Invalid date format (use YYYY-MM-DD or DD/MM/YYYY)'
        });
      }

      if (user.end_date) {
        const endDate = parseDate(user.end_date);
        if (!endDate) {
          errors.push({
            row: rowNum,
            field: 'end_date',
            message: 'Invalid date format (use YYYY-MM-DD or DD/MM/YYYY)'
          });
        } else if (startDate && endDate <= startDate) {
          errors.push({
            row: rowNum,
            field: 'end_date',
            message: 'End date must be after start date'
          });
        }
      }
    }

    // Validate status
    if (user.status && !['active', 'inactive'].includes(user.status.toLowerCase())) {
      errors.push({
        row: rowNum,
        field: 'status',
        message: 'Status must be "active" or "inactive"'
      });
    }

    return {
      ...user,
      validationErrors: errors,
      isValid: errors.length === 0
    };
  });
};

export const formatDateForDB = (dateStr: string): string => {
  const date = parseDate(dateStr);
  if (!date) return '';
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};
